import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Sparkles } from "lucide-react";

export function EmailTemplatesEditor({ canEdit }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [proposal, setProposal] = useState(null);

  const load = () => api.get("/admin/email-templates").then(r => {
    setTemplates(r.data.items || []);
    if (!selected && r.data.items?.length) pick(r.data.items[0]);
  }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: load templates once; `load` is a stable closure
  useEffect(() => { load(); }, []);

  const pick = (t) => {
    setSelected(t);
    setDraft({ subject: t.subject, body: t.body, from_email: t.from_email });
    setProposal(null);
    setAiInstruction("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-template/${selected.key}`, draft);
      toast.success("Template saved");
      const updated = templates.map(t => t.key === selected.key ? { ...t, ...draft } : t);
      setTemplates(updated);
      setSelected({ ...selected, ...draft });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  const sendTest = async () => {
    const to = window.prompt("Send a test email to:", "");
    if (to === null) return;
    try {
      const r = await api.post(`/admin/email-template/${selected.key}/test`, { to: to || undefined });
      toast.success(r.data.sent ? `Test sent to ${r.data.to}` : `Could not deliver to ${r.data.to} — check Resend settings.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test"); }
  };

  const runAi = async () => {
    if (!aiInstruction.trim()) return;
    setAiBusy(true);
    setProposal(null);
    try {
      const r = await api.post(`/admin/email-template/${selected.key}/ai`, {
        instruction: aiInstruction, subject: draft.subject, body: draft.body,
      });
      if (r.data.error) { toast.error(r.data.error); }
      else setProposal(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI helper failed"); }
    setAiBusy(false);
  };

  const applyProposal = () => {
    setDraft(d => ({ ...d, subject: proposal.subject ?? d.subject, body: proposal.body ?? d.body }));
    setProposal(null);
    toast.success("Applied — review and Save to publish.");
  };

  const insertVar = (v) => setDraft(d => ({ ...d, body: `${d.body || ""}{{${v}}}` }));

  if (!selected || !draft) return <div className="text-[#6B7280] text-sm">Loading templates…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6" data-testid="email-templates-editor">
      <div className="space-y-2" data-testid="email-template-list">
        {templates.map(t => (
          <button key={t.key} data-testid={`email-template-${t.key}`} onClick={() => pick(t)}
            className={`w-full text-left border p-3 ${selected.key === t.key ? "border-[#C79A3B] bg-[#F7E6DD]" : "border-[#E5E7EB] bg-white hover:bg-[#FBF3EC]"}`}>
            <div className="flex items-center gap-2 text-sm font-bold"><Mail size={14} className="text-[#C79A3B]" /> {t.name}</div>
            <div className="text-[11px] text-[#6B7280] mt-1 leading-snug">{t.description}</div>
          </button>
        ))}
      </div>

      <div className="border border-[#E5E7EB] bg-white p-5 space-y-5" data-testid="email-template-editor">
        <div>
          <div className="label-eyebrow mb-1">Subject</div>
          <input data-testid="email-subject-input" disabled={!canEdit} value={draft.subject}
            onChange={e => setDraft({ ...draft, subject: e.target.value })}
            className="w-full border border-[#E5E7EB] px-3 py-2 text-sm disabled:bg-[#F4F4F4]" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="label-eyebrow">Body</span>
            <span className="text-[11px] text-[#6B7280]">Blank line = new paragraph</span>
          </div>
          <textarea data-testid="email-body-input" disabled={!canEdit} rows={10} value={draft.body}
            onChange={e => setDraft({ ...draft, body: e.target.value })}
            className="w-full border border-[#E5E7EB] px-3 py-2 text-sm font-mono disabled:bg-[#F4F4F4]" />
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-[11px] text-[#6B7280] self-center">Merge variables:</span>
            {selected.variables.map(v => (
              <button key={v} type="button" data-testid={`email-var-${v}`} disabled={!canEdit} onClick={() => insertVar(v)}
                className="text-[11px] font-mono bg-[#F4F4F4] border border-[#E5E7EB] px-2 py-1 hover:border-[#C79A3B] disabled:opacity-50">{`{{${v}}}`}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="label-eyebrow mb-1">Send from</div>
          <input data-testid="email-from-input" disabled={!canEdit} value={draft.from_email}
            onChange={e => setDraft({ ...draft, from_email: e.target.value })}
            placeholder={`Default: ${selected.default_from}`}
            className="w-full border border-[#E5E7EB] px-3 py-2 text-sm disabled:bg-[#F4F4F4]" />
          <p className="text-[11px] text-[#6B7280] mt-1">Leave blank to use the default ({selected.default_from}). Must be a verified send-as address (e.g. sales@ / quotes@).</p>
        </div>

        {canEdit && (
          <div className="border-t border-[#E5E7EB] pt-4">
            <div className="label-eyebrow mb-2 flex items-center gap-2"><Sparkles size={14} className="text-[#C79A3B]" /> AI copy helper</div>
            <div className="flex gap-2">
              <input data-testid="email-ai-instruction" value={aiInstruction} onChange={e => setAiInstruction(e.target.value)}
                placeholder="e.g. make it warmer and mention our 10-year guarantee"
                className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm" />
              <button data-testid="email-ai-run" disabled={aiBusy || !aiInstruction.trim()} onClick={runAi} className="de-btn-secondary text-xs whitespace-nowrap">{aiBusy ? "Thinking…" : "Improve with AI"}</button>
            </div>
            {proposal && (
              <div className="mt-3 border border-[#C79A3B] bg-[#F4F4F4] p-4" data-testid="email-ai-proposal">
                <div className="text-xs font-bold uppercase tracking-widest text-[#C79A3B] mb-2">Proposed copy</div>
                <div className="text-xs text-[#6B7280] mb-1">Subject</div>
                <div className="text-sm font-semibold mb-2">{proposal.subject}</div>
                <div className="text-xs text-[#6B7280] mb-1">Body</div>
                <pre className="text-sm whitespace-pre-wrap font-sans bg-white border border-[#E5E7EB] p-3">{proposal.body}</pre>
                {proposal.summary && <p className="text-xs text-[#6B7280] mt-2">{proposal.summary}</p>}
                {Array.isArray(proposal.warnings) && proposal.warnings.length > 0 && (
                  <ul className="text-xs text-amber-700 mt-1 list-disc pl-4">{proposal.warnings.map((w, i) => <li key={`${i}-${w}`}>{w}</li>)}</ul>
                )}
                <div className="flex gap-2 mt-3">
                  <button data-testid="email-ai-apply" onClick={applyProposal} className="de-btn-primary text-xs">Apply to editor</button>
                  <button data-testid="email-ai-discard" onClick={() => setProposal(null)} className="de-btn-secondary text-xs">Discard</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 border-t border-[#E5E7EB] pt-4">
          {canEdit && <button data-testid="email-save-btn" disabled={saving} onClick={save} className="de-btn-primary">{saving ? "Saving…" : "Save template"}</button>}
          {canEdit && <button data-testid="email-test-btn" onClick={sendTest} className="de-btn-secondary text-xs">Send test email</button>}
          {!canEdit && <p className="text-xs text-[#6B7280]">Templates are read-only for your role.</p>}
        </div>
      </div>
    </div>
  );
}
